import { useEffect, useRef } from "react";

/**
 * Adds `reveal--visible` when the element enters the viewport (respects reduced motion).
 */
export function useRevealOnScroll(options = {}) {
  const ref = useRef(null);
  const {
    threshold = 0.08,
    rootMargin = "0px 0px -6% 0px",
    once = true,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("reveal--visible");
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add("reveal--visible");
        if (once) observer.unobserve(el);
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return ref;
}

/**
 * Adds a CSS class when the element enters the viewport (for staggered children, etc.).
 */
export function useInViewClass(className, options = {}) {
  const ref = useRef(null);
  const {
    threshold = 0.08,
    rootMargin = "0px 0px -8% 0px",
    once = true,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add(className);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add(className);
        if (once) observer.unobserve(el);
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [className, threshold, rootMargin, once]);

  return ref;
}
